import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { track } from '@/lib/analytics';
import { FiBook, FiImage, FiPlus, FiFilter, FiX } from 'react-icons/fi';
import { apiJson, ApiError } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import type { JournalEntryListItem, JournalEntryType } from '@/lib/types';
import { JournalTagPicker } from '@/components/JournalTagPicker';
import { Button } from '@/components';

type JournalSection = {
  title: string;
  data: JournalEntryListItem[];
};

function parseEntryDate(entryDate: string): { dow: string; dayNum: string } {
  const d = new Date(entryDate + (entryDate.includes('T') ? '' : 'T12:00:00'));
  const dow = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const dayNum = String(d.getDate());
  return { dow, dayNum };
}

function buildSections(entries: JournalEntryListItem[]): JournalSection[] {
  if (entries.length === 0) return [];
  const sorted = [...entries].sort(
    (a, b) => new Date(b.entry_date).getTime() - new Date(a.entry_date).getTime()
  );
  const map = new Map<string, JournalEntryListItem[]>();
  const order: string[] = [];
  for (const e of sorted) {
    const d = new Date(e.entry_date + (e.entry_date.includes('T') ? '' : 'T12:00:00'));
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    const bucket = map.get(key);
    if (bucket) bucket.push(e);
  }
  return order
    .map((key) => {
      const data = map.get(key);
      const first = data?.[0];
      if (!data?.length || !first) return null;
      const d = new Date(first.entry_date + (first.entry_date.includes('T') ? '' : 'T12:00:00'));
      const title = d.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
      return { title, data };
    })
    .filter((s): s is JournalSection => s !== null);
}

function formatTimeShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function footerMeta(item: JournalEntryListItem): string {
  if (item.entry_type === 'PHONE_CALL') {
    const parts: string[] = [];
    const t = formatTimeShort(item.call_start_time);
    if (t) parts.push(t);
    const ct =
      item.call_type === 'FACETIME'
        ? 'FaceTime'
        : item.call_type === 'VIDEO'
          ? 'Video'
          : item.call_type === 'REGULAR'
            ? 'Phone'
            : 'Call';
    parts.push(ct);
    if (item.call_duration_minutes != null) {
      parts.push(`${item.call_duration_minutes} min`);
    }
    return parts.join(' · ');
  }
  if (item.entry_type === 'EXTERNAL_MESSAGE') {
    const parts: string[] = [];
    const t = formatTimeShort(item.external_occurred_at);
    if (t) parts.push(t);
    const ch =
      item.external_channel === 'IMESSAGE'
        ? 'iMessage'
        : item.external_channel === 'IN_PERSON'
          ? 'In person'
          : item.external_channel === 'EMAIL'
            ? 'Email'
            : item.external_channel === 'SMS'
              ? 'SMS'
              : item.external_channel === 'OTHER'
                ? 'Other'
                : 'External';
    parts.push(ch);
    return parts.join(' · ');
  }
  const created = formatTimeShort(item.created_at);
  return created ? `${created}` : 'Note';
}

function previewTitle(item: JournalEntryListItem): string {
  if (item.title?.trim()) return item.title.trim();
  if (item.entry_type === 'PHONE_CALL') return 'Phone call';
  if (item.entry_type === 'EXTERNAL_MESSAGE') return 'External message';
  return 'Journal entry';
}

function previewBody(item: JournalEntryListItem): string {
  const b = item.body?.trim() ?? '';
  if (b.length === 0) return '';
  if (b.length <= 120) return b;
  return b.slice(0, 117) + '…';
}

function attachmentCount(item: JournalEntryListItem): number {
  return item.attachment_count ?? item.attachments?.length ?? 0;
}

function JournalListPage() {
  const { getToken, isSignedIn } = useAuth();
  const [fabOpen, setFabOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [entryTypeFilter, setEntryTypeFilter] = useState<'all' | JournalEntryType>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterTagIds, setFilterTagIds] = useState<number[]>([]);

  const journalQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (entryTypeFilter !== 'all') params.set('entry_type', entryTypeFilter);
    if (filterTagIds.length > 0) params.set('tag', filterTagIds.join(','));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [startDate, endDate, entryTypeFilter, filterTagIds]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (entryTypeFilter !== 'all') n++;
    if (startDate || endDate) n++;
    if (filterTagIds.length > 0) n++;
    return n;
  }, [entryTypeFilter, startDate, endDate, filterTagIds]);

  useEffect(() => {
    track('Journal Screen');
  }, []);

  const { currentHouseholdId } = useHouseholds();
  const entriesQuery = useQuery({
    queryKey: ['journal-entries', currentHouseholdId, journalQueryString],
    queryFn: async () => {
      try {
        return await apiJson<JournalEntryListItem[]>(
          getToken,
          `/v1/journal${journalQueryString}`
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: !!isSignedIn,
  });

  const sections = useMemo(
    () => buildSections(entriesQuery.data ?? []),
    [entriesQuery.data]
  );

  return (
    <div className='min-h-screen bg-sky-light pb-24 lg:pb-8'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-6 py-4 flex items-center justify-between gap-4'>
          <h1
            className='text-2xl font-light text-primary tracking-tight'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Journal
          </h1>
          <div className='relative flex items-center gap-2'>
            <button
              type='button'
              onClick={() => setFilterOpen(true)}
              className='relative w-10 h-10 rounded-full border border-gray-200 bg-white flex items-center justify-center text-primary hover:bg-primary/5'
              aria-label='Filter journal'
            >
              <FiFilter className='w-5 h-5' />
              {activeFilterCount > 0 && (
                <span className='absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center'>
                  {activeFilterCount}
                </span>
              )}
            </button>
            {fabOpen ? (
              <button
                type='button'
                className='fixed inset-0 z-20 bg-black/20'
                aria-label='Close menu'
                onClick={() => setFabOpen(false)}
              />
            ) : null}
            {fabOpen ? (
              <div className='absolute right-0 top-full mt-2 z-30 flex flex-col gap-2 items-end'>
                <Link
                  to='/app/journal/record'
                  search={{ entryType: 'GENERAL' }}
                  onClick={() => setFabOpen(false)}
                  className='bg-white text-primary font-semibold px-4 py-2 rounded-full shadow-lg border border-gray-100 whitespace-nowrap'
                  style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
                >
                  General entry
                </Link>
                <Link
                  to='/app/journal/record'
                  search={{ entryType: 'PHONE_CALL' }}
                  onClick={() => setFabOpen(false)}
                  className='bg-white text-primary font-semibold px-4 py-2 rounded-full shadow-lg border border-gray-100 whitespace-nowrap'
                  style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
                >
                  Phone call
                </Link>
                <Link
                  to='/app/journal/record'
                  search={{ entryType: 'EXTERNAL_MESSAGE' }}
                  onClick={() => setFabOpen(false)}
                  className='bg-white text-primary font-semibold px-4 py-2 rounded-full shadow-lg border border-gray-100 whitespace-nowrap'
                  style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
                >
                  External message
                </Link>
              </div>
            ) : null}
            <button
              type='button'
              onClick={() => {
                if (!fabOpen) track('Journal FAB Tapped');
                setFabOpen(!fabOpen);
              }}
              className='w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-md hover:opacity-95'
              aria-expanded={fabOpen}
            >
              {fabOpen ? (
                <span className='text-xl leading-none'>×</span>
              ) : (
                <FiPlus className='w-6 h-6' />
              )}
            </button>
          </div>
        </div>
      </header>

      <div className='max-w-2xl mx-auto px-6 py-4'>
        <p
          className='text-center text-sm text-gray-500 mb-4'
          style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
        >
          Private to you — not shared with your co-parent
        </p>

        {entriesQuery.isLoading && (
          <p className='text-center text-gray-500 py-12'>Loading journal…</p>
        )}

        {entriesQuery.isError && (
          <div className='text-center py-12 text-gray-500'>
            <p className='font-semibold text-gray-700'>Couldn&apos;t load journal</p>
            <button
              type='button'
              onClick={() => void entriesQuery.refetch()}
              className='mt-4 text-primary font-semibold underline'
            >
              Try again
            </button>
          </div>
        )}

        {!entriesQuery.isLoading &&
          !entriesQuery.isError &&
          (entriesQuery.data?.length ?? 0) === 0 && (
          <div className='text-center py-16 px-4'>
            <FiBook className='w-12 h-12 mx-auto text-gray-300' />
            <p
              className='mt-4 text-lg font-bold text-gray-800'
              style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
            >
              No journal entries yet
            </p>
            <p className='mt-2 text-gray-500 max-w-sm mx-auto text-sm'>
              Tap + to add a general note or phone call. Only you can see these entries.
            </p>
          </div>
        )}

        {!entriesQuery.isLoading &&
          !entriesQuery.isError &&
          sections.map((section) => (
            <section key={section.title} className='mb-6'>
              <h2
                className='text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-2'
                style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
              >
                {section.title}
              </h2>
              <ul className='space-y-1.5'>
                {section.data.map((item) => {
                  const { dow, dayNum } = parseEntryDate(item.entry_date);
                  const ac = attachmentCount(item);
                  const snippet = previewBody(item);
                  const meta = footerMeta(item);
                  return (
                    <li key={item.id}>
                      <Link
                        to='/app/journal/$entryId'
                        params={{ entryId: String(item.id) }}
                        className='flex gap-3 bg-white rounded-xl border border-gray-100 p-3 hover:border-primary/30 transition-colors'
                      >
                        <div className='w-11 shrink-0 text-center pt-0.5'>
                          <div className='text-[10px] font-bold text-gray-400 tracking-wide'>{dow}</div>
                          <div
                            className='text-[26px] font-bold text-gray-900 leading-tight'
                            style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
                          >
                            {dayNum}
                          </div>
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p
                            className='font-bold text-gray-900 line-clamp-2'
                            style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
                          >
                            {previewTitle(item)}
                          </p>
                          {snippet ? (
                            <p className='text-sm text-gray-500 mt-1 line-clamp-2'>{snippet}</p>
                          ) : null}
                          {(item.tags ?? []).length > 0 ? (
                            <div className='flex flex-wrap gap-1 mt-2'>
                              {(item.tags ?? []).map((tag) => (
                                <span
                                  key={tag.id}
                                  className='text-[10px] font-bold px-2 py-0.5 rounded-full text-white'
                                  style={{ backgroundColor: tag.color }}
                                >
                                  {tag.label}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          {meta ? <p className='text-xs text-gray-400 mt-2'>{meta}</p> : null}
                        </div>
                        {ac > 0 ? (
                          <div className='relative w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0'>
                            <FiImage className='w-5 h-5 text-primary opacity-90' />
                            {ac > 1 ? (
                              <span className='absolute bottom-0.5 right-0.5 min-w-[18px] h-[18px] rounded-full bg-black/55 text-white text-[10px] font-bold flex items-center justify-center px-1'>
                                {ac}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <div className='w-12 shrink-0' />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
      </div>

      {filterOpen && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-xl font-bold text-gray-900'>Filter journal</h2>
              <button type='button' onClick={() => setFilterOpen(false)} className='p-2 hover:bg-gray-100 rounded-full'>
                <FiX className='w-5 h-5' />
              </button>
            </div>
            <div className='space-y-4'>
              <div>
                <p className='text-sm font-semibold text-gray-600 mb-2'>Entry type</p>
                <div className='flex rounded-xl border border-gray-200 overflow-hidden'>
                  {(['all', 'GENERAL', 'PHONE_CALL', 'EXTERNAL_MESSAGE'] as const).map((opt) => (
                    <button
                      key={opt}
                      type='button'
                      onClick={() => setEntryTypeFilter(opt)}
                      className={`flex-1 py-2 text-sm font-semibold ${
                        entryTypeFilter === opt
                          ? 'bg-primary text-white'
                          : 'bg-gray-50 text-gray-600'
                      }`}
                    >
                      {opt === 'all'
                        ? 'All'
                        : opt === 'GENERAL'
                          ? 'Notes'
                          : opt === 'PHONE_CALL'
                            ? 'Calls'
                            : 'External'}
                    </button>
                  ))}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='block text-sm font-semibold text-gray-600 mb-1'>From</label>
                  <input
                    type='date'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className='w-full px-3 py-2.5 rounded-xl border border-gray-200'
                  />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-gray-600 mb-1'>To</label>
                  <input
                    type='date'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className='w-full px-3 py-2.5 rounded-xl border border-gray-200'
                  />
                </div>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-600 mb-2'>Tags</p>
                <JournalTagPicker
                  selectedTagIds={filterTagIds}
                  onChange={setFilterTagIds}
                  showManage={false}
                />
              </div>
            </div>
            <div className='flex gap-3 mt-6'>
              {activeFilterCount > 0 && (
                <Button
                  variant='secondary'
                  fullWidth
                  onClick={() => {
                    setEntryTypeFilter('all');
                    setStartDate('');
                    setEndDate('');
                    setFilterTagIds([]);
                  }}
                >
                  Clear
                </Button>
              )}
              <Button fullWidth onClick={() => setFilterOpen(false)}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/journal/')({
  component: JournalListPage,
});
